import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../services/api.js';

const initialState = {
  records: [],
  total: 0,
  page: 1,
  limit: 20,
  loading: true,
  error: '',
  search: '',
  selectedRecord: null,
  viewMode: 'records',
};

export const fetchProviderRecords = createAsyncThunk(
  'provider/fetchRecords',
  async (_, { getState, rejectWithValue }) => {
    const { provider } = getState();
    try {
      const res = await api.get('/records', {
        params: {
          page: provider.page,
          limit: provider.limit,
          search: provider.search.trim() || undefined,
        },
      });

      const { data, total, limit } = res.data || {};
      return { records: data || [], total: total || 0, limit: limit || provider.limit };
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || 'Failed to load patients.';
      return rejectWithValue({ message });
    }
  }
);

export const updateProviderRecord = createAsyncThunk(
  'provider/updateRecord',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await api.put(`/records/${id}`, data);
      return res.data;
    } catch (err) {
      const message =
        err?.response?.data?.message || err?.message || 'Failed to update record.';
      return rejectWithValue({ message });
    }
  }
);

const providerSlice = createSlice({
  name: 'provider',
  initialState,
  reducers: {
    setPage(state, action) {
      state.page = action.payload;
    },
    setLimit(state, action) {
      state.limit = action.payload;
    },
    setSearch(state, action) {
      state.search = action.payload;
    },
    setSelectedRecord(state, action) {
      state.selectedRecord = action.payload;
    },
    setViewMode(state, action) {
      state.viewMode = action.payload;
    },
    clearError(state) {
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProviderRecords.pending, (state) => {
        state.loading = true;
        state.error = '';
      })
      .addCase(fetchProviderRecords.fulfilled, (state, action) => {
        state.records = action.payload.records;
        state.total = action.payload.total;
        state.limit = action.payload.limit;
        state.loading = false;
      })
      .addCase(fetchProviderRecords.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to load patients.';
        state.records = [];
        state.total = 0;
        state.loading = false;
      })
      .addCase(updateProviderRecord.fulfilled, (state, action) => {
        const updated = action.payload;
        state.records = state.records.map((record) =>
          record._id === updated._id ? updated : record
        );
        state.selectedRecord = updated;
      })
      .addCase(updateProviderRecord.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to update record.';
      });
  },
});

export const {
  setPage,
  setLimit,
  setSearch,
  setSelectedRecord,
  setViewMode,
  clearError,
} = providerSlice.actions;

export const selectProviderRecords = (state) => state.provider.records;
export const selectProviderTotal = (state) => state.provider.total;
export const selectProviderPage = (state) => state.provider.page;
export const selectProviderLimit = (state) => state.provider.limit;
export const selectProviderLoading = (state) => state.provider.loading;
export const selectProviderError = (state) => state.provider.error;
export const selectProviderSearch = (state) => state.provider.search;
export const selectProviderSelectedRecord = (state) => state.provider.selectedRecord;
export const selectProviderViewMode = (state) => state.provider.viewMode;

export default providerSlice.reducer;
